import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";

export interface CommissionPayment {
  _id: string;
  businessDeveloper: { _id: string; fullName: string; email: string } | string;
  amount: number;
  paidOn: string;
  note: string;
  createdAt: string;
}

interface State {
  payments: CommissionPayment[];
  mySettled: number;
  loading: boolean;
  saving: boolean;
  fetchPayments: (bdId?: string) => Promise<void>;
  createPayment: (data: {
    businessDeveloper: string;
    amount: number;
    paidOn?: string;
    note?: string;
  }) => Promise<boolean>;
  deletePayment: (id: string) => Promise<void>;
  fetchMySettled: () => Promise<void>;
}

export const useCommissionStore = create<State>((set, get) => ({
  payments: [],
  mySettled: 0,
  loading: false,
  saving: false,

  fetchPayments: async (bdId) => {
    set({ loading: true });
    try {
      const { data } = await axiosInstance.get("/commission-payments", {
        params: bdId ? { bdId } : {},
      });
      set({ payments: data.items || [] });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load payments");
    } finally {
      set({ loading: false });
    }
  },

  createPayment: async (payload) => {
    set({ saving: true });
    try {
      await axiosInstance.post("/commission-payments", payload);
      toast.success("Commission settlement recorded");
      await get().fetchPayments();
      return true;
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to record payment");
      return false;
    } finally {
      set({ saving: false });
    }
  },

  deletePayment: async (id) => {
    try {
      await axiosInstance.delete(`/commission-payments/${id}`);
      set({ payments: get().payments.filter((p) => p._id !== id) });
      toast.success("Payment removed");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete payment");
    }
  },

  fetchMySettled: async () => {
    try {
      const { data } = await axiosInstance.get("/commission-payments/mine/summary");
      set({ mySettled: data.settled || 0 });
    } catch {
      // non-critical
    }
  },
}));
